import React, { ComponentProps, useCallback, useState } from 'react';

import { FormControl, IconProps, Input, InputProps } from '@rbilabs/universal-components';
import { Keyboard, NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';

import { useLogUserErrorMessage } from 'hooks/log-user-error-message';
import { t } from 'state/intl/translations';
import { BODY_MAX_FONT_SIZE_MULTIPLIER } from 'styles/constants/fonts';
import { TraceName, startTrace } from 'utils/performance/trace';

import { DropdownContent } from './dropdown-content';
import { Container, StyledAddressInput, StyledInputIcon } from './styles';
import { AutocompleteDropdownProps, AutocompletePrediction } from './types';

type ITextInputProps = ComponentProps<typeof Input> & {
  label: string;
  labelColor?: string;
  inputCustomProps?: InputProps;
  rightIconCustomProps?: IconProps;
};

const AutocompleteOptionsDropdown = React.forwardRef<
  HTMLInputElement,
  ITextInputProps & AutocompleteDropdownProps
>(
  (
    {
      testID,
      clearTextIconTitle,
      errorMessage,
      label,
      labelColor,
      noPredictionsFoundText,
      predictions,
      onInputChange,
      onSelection,
      value,
      onBlur,
      inputCustomProps,
      rightIconCustomProps,
      inputWrapper,
      maskSuggestionsForSessionReplay,
    },
    ref
  ) => {
    const [highlightedItemId, setHighlightedItemId] = useState<string | null>(null);
    const [isItemSelected, setIsItemSelected] = useState(false);
    const [isInputFocus, setIsInputFocus] = useState(false);

    const handleKeyPress = (event: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
      if (!predictions) {
        return;
      }

      const key = event.nativeEvent.key;
      const currentIndex = predictions?.findIndex(item => item.placeId === highlightedItemId);

      switch (key) {
        case 'ArrowUp':
          if (currentIndex > 0) {
            setHighlightedItemId(predictions[currentIndex - 1].placeId);
          }
          break;
        case 'ArrowDown':
          if (currentIndex < predictions?.length - 1) {
            setHighlightedItemId(predictions[currentIndex + 1].placeId);
          }
          break;
        case 'Enter':
          if (highlightedItemId) {
            handleOnSelectItem(predictions[currentIndex]);
          }
          break;
        case 'Escape':
          setIsInputFocus(false);
          setHighlightedItemId(null);
          Keyboard.dismiss();
          break;
      }
    };

    const handleOnSelectItem = useCallback(
      (item: AutocompletePrediction) => {
        startTrace(TraceName.STORE_LOCATOR_MANUAL_SEARCH_LOCATION_PRESSED_TO_LIST_ITEM_DISPLAYED);
        onSelection({
          address: item.description || '',
          placeId: item.placeId || '',
          placeData: item.placeData,
        });
        setIsItemSelected(true);
        Keyboard.dismiss();
      },
      [onSelection]
    );

    const handleOptionPress = useCallback(
      (item: AutocompletePrediction) => {
        handleOnSelectItem(item);
        setIsInputFocus(false);
      },
      [handleOnSelectItem]
    );

    const InputRightIcon = () => {
      const accessibilityLabel = t('searchLocation');
      const accessibilityLabelClear = t('clearText');
      return value ? (
        <StyledInputIcon
          variant="cancel"
          title={clearTextIconTitle}
          // @ts-expect-error TS(2345) FIXME: Argument of type '{}' is not assignable to paramet... Remove this comment to see the full error message
          onPress={() => handleOnSelectItem({})}
          _web={{ cursor: 'pointer' }}
          focusable
          accessibilityLabel={accessibilityLabelClear}
          rightIconCustomProps={rightIconCustomProps}
        />
      ) : (
        <StyledInputIcon
          variant="search"
          accessibilityLabel={accessibilityLabel}
          rightIconCustomProps={rightIconCustomProps}
        />
      );
    };

    const handleInputChange = (inputValue: string) => {
      onInputChange(inputValue);
      setIsItemSelected(false);
    };

    const handleInputFocus = () => {
      setIsInputFocus(true);
    };

    useLogUserErrorMessage({ message: errorMessage });

    const shouldShowDropdown = isInputFocus && value && value.length > 2 && !isItemSelected;

    const addressInput = (
      <StyledAddressInput
        testID={testID}
        maxFontSizeMultiplier={BODY_MAX_FONT_SIZE_MULTIPLIER}
        placeholder={label}
        placeholderTextColor={labelColor}
        value={value}
        InputRightElement={InputRightIcon()}
        onChangeText={handleInputChange}
        wrapperRef={ref}
        onFocus={handleInputFocus}
        onBlur={onBlur}
        inputCustomProps={inputCustomProps}
        onKeyPress={handleKeyPress}
      />
    );

    return (
      <Container>
        <FormControl isInvalid={!!errorMessage} margin={0}>
          {inputWrapper ? inputWrapper(addressInput) : addressInput}
          <FormControl.ErrorMessage>{errorMessage}</FormControl.ErrorMessage>
        </FormControl>
        {shouldShowDropdown && (
          <DropdownContent
            predictions={predictions}
            noPredictionsFoundText={noPredictionsFoundText}
            highlightedItemId={highlightedItemId}
            onSelect={handleOptionPress}
            maskSuggestionsForSessionReplay={maskSuggestionsForSessionReplay}
          />
        )}
      </Container>
    );
  }
);

export default AutocompleteOptionsDropdown;

export * from './types';
